/**
 * One in-flight GET per card/back URL so Vue, Phaser, and Three.js share
 * a single decoded HTMLImageElement.
 *
 * Critical path: decode from the HTTP URL (browser cache / SW). Do not wait
 * on Cache Storage — that open/match round-trip is why pack reveals and
 * duel sprites sat on a spinner. Cache Storage is only a fallback if the
 * Image request fails.
 *
 * Same Cache Storage name as `public/sw.js` so Login/Home warmup fills the
 * disk cache the SW reads on the next `/card-images/` GET.
 */

import { allCatalogCards } from '../game/packs/packCatalog'
import { getLocalCardImageUrl } from './cardImages'

/** Must match `CARD_CACHE` in `client/public/sw.js`. */
const CARD_ART_CACHE = 'dc-card-images-v6'
const WARM_CONCURRENCY = 4

const elements = new Map<string, HTMLImageElement>()
const inflight = new Map<string, Promise<HTMLImageElement>>()
const failed = new Set<string>()
/** Bumped by {@link forgetCardImage} so an in-flight decode cannot repopulate. */
const imageEpoch = new Map<string, number>()
let warmCatalogPromise: Promise<void> | null = null

/**
 * Sleeves and the house back. A failed decode must be retried: the phone
 * app drops these bitmaps, and a sticky failure paints black card backs
 * for the rest of the tab.
 */
function isSleeveArt(url: string): boolean {
  return (
    url.includes('/assets/sleeves/') || url.includes('/assets/cards/backs/')
  )
}

/**
 * Drop a decoded image so the next load fetches and decodes again.
 * Used when the app returns and the old bitmap may paint black.
 */
export function forgetCardImage(url: string): void {
  imageEpoch.set(url, (imageEpoch.get(url) ?? 0) + 1)
  elements.delete(url)
  failed.delete(url)
  inflight.delete(url)
}

/**
 * Return a previously decoded image, if this URL was already loaded.
 */
export function peekCardImage(url: string): HTMLImageElement | undefined {
  return elements.get(url)
}

function absoluteUrl(url: string): string {
  if (typeof window === 'undefined') return url
  return new URL(url, window.location.origin).href
}

/**
 * Decode `src` (http or blob) into an HTMLImageElement.
 */
function decodeHtmlImage(src: string): Promise<HTMLImageElement> {
  const img = new Image()
  img.decoding = 'async'
  const viaLoad = new Promise<HTMLImageElement>((resolve, reject) => {
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Failed to decode ${src}`))
  })
  img.src = src
  if (img.complete && img.naturalWidth > 0) return Promise.resolve(img)
  if (typeof img.decode === 'function') {
    return img.decode().then(() => img).catch(() => viaLoad)
  }
  return viaLoad
}

async function blobFromCacheStorage(url: string): Promise<Blob | null> {
  if (typeof caches === 'undefined') return null
  try {
    const cache = await caches.open(CARD_ART_CACHE)
    const hit = await cache.match(absoluteUrl(url))
    if (hit?.ok) return hit.blob()
  } catch {
    return null
  }
  return null
}

/**
 * Fetch `url` once, decode it, and reuse the same HTMLImageElement.
 * Callers must not revoke `img.src` while anything still displays it.
 */
export function loadCardImage(url: string): Promise<HTMLImageElement> {
  const hit = elements.get(url)
  if (hit) return Promise.resolve(hit)
  if (failed.has(url)) {
    return Promise.reject(new Error(`Failed to decode ${url}`))
  }
  const pending = inflight.get(url)
  if (pending !== undefined) return pending

  const promise = (async () => {
    const epoch = imageEpoch.get(url) ?? 0
    const remember = (img: HTMLImageElement): HTMLImageElement => {
      if ((imageEpoch.get(url) ?? 0) !== epoch) return img
      elements.set(url, img)
      failed.delete(url)
      return img
    }
    const stickFailure = (): void => {
      if ((imageEpoch.get(url) ?? 0) !== epoch) return
      if (isSleeveArt(url)) return
      failed.add(url)
    }
    try {
      const img = await decodeHtmlImage(url)
      return remember(img)
    } catch (err) {
      const blob = await blobFromCacheStorage(url)
      if (!blob) {
        stickFailure()
        throw err
      }
      const blobUrl = URL.createObjectURL(blob)
      try {
        const img = await decodeHtmlImage(blobUrl)
        return remember(img)
      } catch (inner) {
        URL.revokeObjectURL(blobUrl)
        stickFailure()
        throw inner
      }
    }
  })()

  inflight.set(url, promise)
  void promise.finally(() => {
    if (inflight.get(url) === promise) inflight.delete(url)
  })
  return promise
}

/**
 * Decode every URL (shared cache). Resolves even if some fetches fail.
 */
export function loadCardImages(
  urls: string[],
): Promise<PromiseSettledResult<HTMLImageElement>[]> {
  return Promise.allSettled(urls.map((url) => loadCardImage(url)))
}

/**
 * Puts one JPEG into Cache Storage (or warms HTTP cache if Cache API is
 * missing). Does not decode the bitmap into RAM.
 */
async function persistCardJpeg(
  url: string,
  cache: Cache | null,
): Promise<void> {
  if (!cache) {
    await fetch(url, { credentials: 'same-origin', cache: 'reload' })
    return
  }
  const abs = absoluteUrl(url)
  const hit = await cache.match(abs)
  if (hit?.ok) return
  const res = await fetch(url, { credentials: 'same-origin', cache: 'reload' })
  if (!res.ok) return
  try {
    await cache.put(abs, res.clone())
  } catch {
    /* quota / private mode */
  }
}

/**
 * Download every catalog scan into Cache Storage. Safe to call often: one
 * flight per tab. Does not decode the full set into HTMLImageElements.
 */
export function warmCatalogCardArt(): Promise<void> {
  if (warmCatalogPromise) return warmCatalogPromise
  warmCatalogPromise = (async () => {
    const urls = allCatalogCards().map((card) => getLocalCardImageUrl(card.id))
    let cache: Cache | null = null
    if (typeof caches !== 'undefined') {
      try {
        cache = await caches.open(CARD_ART_CACHE)
      } catch {
        cache = null
      }
    }
    for (let i = 0; i < urls.length; i += WARM_CONCURRENCY) {
      const batch = urls.slice(i, i + WARM_CONCURRENCY)
      await Promise.allSettled(
        batch.map((url) => persistCardJpeg(url, cache)),
      )
    }
  })()
  return warmCatalogPromise
}
